import React, { FC, useEffect, useRef, useState } from 'react';
import TileItem, { calculateMeeplePos } from './TileItem';
import TilePlaceholder from './TilePlaceholder';
import classes from './TileMap.module.scss';
import Meeple from './Meeple';
import { CSSTransition } from 'react-transition-group';
import { CcsonEnvDTO, CcsonPlayerDTO, RecoveredMeeplesDTO, TileDTO } from '../../types/ccson_env';
import { useDispatch, useSelector } from 'react-redux';
import { ActionDTO, BestActionDTO, DEFAULT_AGENT_SETTINGS, GameEventDTO } from '@envclient/envcore';
import { TileBasedGameFamilyType } from '../../config/tileGameConfig';
import { getTileHeight, getTileWidth } from './TileAsset';
import TileAnchor from './TileAnchor';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { FiRotateCcw } from 'react-icons/fi';
import MeepleCont from './MeepleCont';
import { TileDef, getTileDef } from '../../config/tileMapConfig';
import { useInterval } from 'react-use';
import { MeepleDTO, MeepleType } from '@envclient/envengine/src/envs/tilebase/Meeple';
import PrismaZoom from '../../components/PrismaZoom';
import Button from '../../components/Button';
import { useMyPlayer } from '../../hooks/useMyPlayer';
import { RootState } from '../../reducers/store';
import { consumeGameEvent } from '../../reducers/gameActions';
import { cloneObject } from '../../utils/utils';
import { CSSPos, Pos } from '../../types/frontendTypes';
// bundled as a separate Web Worker chunk by worker-loader (inline loader syntax keeps the app
// CommonJS, avoiding the ESM circular-dep issues). See worker-loader.d.ts.
// eslint-disable-next-line import/no-webpack-loader-syntax
import CcsonAgentWorker from 'worker-loader!./ccsonAgent.worker';

export type Positions = Record<string, { coord: string; actions: TileDTO[]; x: number; y: number }>;

export type SelectPosActions<A extends ActionDTO = ActionDTO> = { selectedPos: Pos; posActions: A[] };

interface TileMapProps {
  tileData: Array<TileDTO>;
  currentActions: Array<TileDTO>;
  lastTile: TileDTO;
  onSubmit: (action?: TileDTO) => void;
  active: boolean;
}

const TileMap: FC<TileMapProps> = (props) => {
  const { tileData, currentActions, lastTile, active, onSubmit } = props;

  const [submitted, setSubmitted] = useState<boolean>(false);
  const game = useSelector((state: RootState) => state.game.game);

  const submitBtnEnabled = active && game?.status === 'PLAYING' && !submitted;

  const [selectedAction, setSelectedAction] = useState<TileDTO | null>(null);
  const [bestAction, setBestAction] = useState<ActionDTO | null>(null);

  const workerRef = useRef<Worker | null>(null);
  const computingRef = useRef<boolean>(false);
  // bumped on every new turn; a worker reply computed under an older generation must NOT be applied
  const computeGenRef = useRef<number>(0);
  // monotonic id per compute request; the worker echoes it so we apply ONLY the latest reply
  const reqIdRef = useRef<number>(0);
  const [recoveredMeeplesEvent, setRecoveredMeeples] = useState<RecoveredMeeplesDTO | null>(null);
  const events = useSelector((state: RootState) => state.game.events) as unknown as GameEventDTO[];
  const env = useSelector((state: RootState) => state.game.env) as CcsonEnvDTO;
  const family = env.gameConfig.family as TileBasedGameFamilyType;
  const dispatch = useDispatch();
  const [positions, setPositions] = useState<Positions>({});

  const myPlayer = useMyPlayer();
  // this client's own seat config, set via the Agent Settings modal (broadcast in PlayerDTO).
  const savedAgentSettings = myPlayer?.agentSettings;
  const myAgentSettings = savedAgentSettings ?? DEFAULT_AGENT_SETTINGS;
  // agent is opt-in only - off by default (DEFAULT_AGENT_SETTINGS.useAgent) until explicitly enabled.
  const agentEnabled = myAgentSettings.useAgent;
  // pause/resume the agent DURING the game (local runtime toggle; resets to running on reload).
  const [agentPaused, setAgentPaused] = useState<boolean>(false);
  // run the agent in a browser Web Worker (the public default). computeOnClient stays true unless the
  // hidden server option is set; when the agent is off/paused nothing computes.
  const clientBotCompute = agentEnabled && myAgentSettings.computeOnClient;
  // TRUE OVERRIDE: once the human touches the board/controls after the agent proposed a move, they've
  // taken over THIS turn - cancel the pending auto-submit and stop the agent recomputing, so their
  // own move stands and they submit it. Ref mirrors the state for the async worker-reply guard.
  const [userTookOver, setUserTookOver] = useState<boolean>(false);
  const userTookOverRef = useRef<boolean>(false);
  const takeOverFromAgent = () => {
    if (!agentEnabled) return;
    userTookOverRef.current = true;
    setUserTookOver(true);
  };
  const meeples = (myPlayer as CcsonPlayerDTO)?.meeples;
  const [preferredMeepleType, setPreferredMeepleType] = useState<MeepleType>();

  const [selectedPos, setSelectedPos] = useState<Pos | null>(null);
  const [posActions, setPosActions] = useState<TileDTO[] | null>(null);

  const [zoom, setZoom] = useState<number>(0.5);

  const tileWidth = getTileWidth();
  const tileHeight = getTileHeight();

  const canvasRef = useRef<HTMLDivElement>(null);
  // mirror of positions for the pointer-up drop handler (avoids stale closures)
  const positionsRef = useRef<Positions>({});
  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  // pixels the pointer must travel before a press becomes a drag (taps keep the click flow)
  const DRAG_THRESHOLD_PX = 6;
  const [dragPos, setDragPos] = useState<CSSPos | null>(null);

  useEffect(
    function currentActionsChanged() {
      // clear state
      setSelectedAction(null);
      setBestAction(null);
      setSelectedPos(null);
      setSubmitted(false);
      userTookOverRef.current = false;
      setUserTookOver(false);
      computingRef.current = false;
      computeGenRef.current += 1;
      // group actions by position
      const positions: Positions = {};
      currentActions.forEach((a) => {
        if (a.x !== null || a.y !== null) {
          const coord = a.x + ':' + a.y;
          if (!positions[coord]) {
            positions[coord] = { coord, actions: [], x: a.x, y: a.y };
          }
          positions[coord].actions.push(a);
        }
      });
      setPositions(positions);
    },
    [currentActions]
  );

  const tilePlaces = Object.keys(positions).map((k) => positions[k]);

  // why???
  const selectedTilePos = cloneObject(selectedAction);
  if (selectedTilePos && selectedPos) {
    selectedTilePos.x = selectedPos.x;
    selectedTilePos.y = selectedPos.y;
  }

  //reset state when action changes
  useEffect(() => {
    if (selectedAction === null) {
      setSelectedPos(null);
      setPosActions(null);
    }
  }, [selectedAction, positions]);

  useEffect(
    function processEvents() {
      if (events?.length === 0) return;
      // RECOVERED_MEEPLES
      // @ts-expect-error type
      const meeplesEvent = events.filter((e) => e.type === 'RECOVERED_MEEPLES')[0] as unknown as RecoveredMeeplesDTO;
      if (meeplesEvent) {
        setRecoveredMeeples(meeplesEvent);
        dispatch(consumeGameEvent(meeplesEvent as unknown as GameEventDTO));
      }
      // BEST_ACTION (server-compute path). When this seat computes in-browser (agentSettings
      // .computeOnClient) the client computes its own move below and the server sends no BEST_ACTION.
      const bestActionEvent = events.filter((e) => e.type === 'BEST_ACTION')[0] as BestActionDTO;
      if (bestActionEvent) {
        dispatch(consumeGameEvent(bestActionEvent));
        if (!clientBotCompute) applyBotAction(bestActionEvent.bestAction as TileDTO);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, dispatch, positions, active, currentActions, submitted, clientBotCompute]
  );

  // Apply a bot action (from the server BEST_ACTION event OR local client-side compute): select it
  // for display and stash it as the pending action that handleSubmit / the auto-submit sends.
  const applyBotAction = (action: TileDTO) => {
    setSelectedAction(action);
    if (!active || submitted) return;
    if (!currentActions.find((a) => a.id === action.id)) return;
    setBestAction(action);
    setSelectedPos({ x: action.x, y: action.y });
    // prefer the `positions` state (already grouped/ordered) but fall back to deriving straight from
    // currentActions - `positions` is rebuilt by a SEPARATE effect one render behind currentActions,
    // so right after currentActions changes (e.g. a fast worker reply on this same turn) `positions`
    // can still be stale/empty and this lookup would silently miss, leaving posActions unset forever.
    // currentActions itself has no such lag - it's the prop that triggered this whole render.
    const posActionsAtCoord =
      positions?.[action.x + ':' + action.y]?.actions ??
      currentActions.filter((a) => a.x === action.x && a.y === action.y);
    if (posActionsAtCoord.length > 0) setPosActions(posActionsAtCoord);
  };

  // Create/destroy the client-compute Web Worker (only when this seat computes in-browser).
  useEffect(() => {
    if (!clientBotCompute) return;
    const worker = new CcsonAgentWorker();
    workerRef.current = worker;
    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, [clientBotCompute]);

  // Client-compute path: when it's this bot's turn, score currentActions in the worker (off the
  // main thread), then apply + auto-submit the best like the server-event path.
  useEffect(
    function computeBotActionClientSide() {
      const worker = workerRef.current;
      if (!clientBotCompute || !worker || agentPaused) return;
      if (!active || userTookOverRef.current) return; // human took over this turn -> don't recompute
      if (bestAction || submitted || computingRef.current || game?.status !== 'PLAYING') return;
      if (!env?.clone || !currentActions || currentActions.length === 0) return; // nothing to compute yet
      computingRef.current = true;
      const gen = computeGenRef.current;
      const reqId = ++reqIdRef.current; // only THIS (latest) request's reply may be applied
      // single persistent handler (overwritten per post), filtered by reqId so a stale/superseded
      // reply is ignored (NOT addEventListener, which fires every handler per message).
      worker.onmessage = (e: MessageEvent) => {
        const data = e.data as { action: TileDTO | null; reqId?: number };
        if (data.reqId !== reqId) return; // superseded by a newer compute
        computingRef.current = false;
        if (computeGenRef.current !== gen) return; // turn advanced while computing - stale
        if (userTookOverRef.current) return; // human took over mid-compute - don't clobber their move
        if (data.action) applyBotAction(data.action);
      };
      // send the raw server clone (the serialized env from CcsonEnv.data), the proven worker input.
      // mcts=true runs the ccson MCTS agent; else the 1-ply heuristic. tuning applies to mcts only.
      const mcts = myAgentSettings.agentType === 'mcts';
      worker.postMessage({ env: env.clone, mcts, tuning: myAgentSettings.mctsTuning, reqId });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientBotCompute, agentPaused, active, env, bestAction, submitted, game?.status, currentActions]
  );

  const placeholderClick = ({ selectedPos, posActions }: SelectPosActions) => {
    takeOverFromAgent();
    const defaultAction = posActions[0];
    setSelectedAction(defaultAction as TileDTO);
    setSelectedPos(selectedPos);
    setPosActions(posActions as TileDTO[]);
  };

  // screen (client) coords -> canvas-local coords (undo PrismaZoom translate + scale)
  const toCanvasLocal = (clientX: number, clientY: number) => {
    const canvasRect = canvasRef.current!.getBoundingClientRect();
    return { x: (clientX - canvasRect.left) / zoom, y: (clientY - canvasRect.top) / zoom };
  };

  // resolve the placeholder under the cursor and place the tile there (reuses the click flow)
  const handleDrop = (clientX: number, clientY: number) => {
    if (!canvasRef.current) return;
    const placeholders = canvasRef.current.querySelectorAll<HTMLElement>('[data-ph-coord]');
    let hitCoord: string | null = null;
    let bestDist = Infinity;
    placeholders.forEach((el) => {
      const r = el.getBoundingClientRect();
      const inside = clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
      if (!inside) return;
      const dx = clientX - (r.left + r.width / 2);
      const dy = clientY - (r.top + r.height / 2);
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        hitCoord = el.getAttribute('data-ph-coord');
      }
    });
    if (!hitCoord) return;
    const entry = positionsRef.current[hitCoord];
    if (!entry) return;
    placeholderClick({ selectedPos: { x: entry.x, y: entry.y }, posActions: entry.actions });
  };

  // pointer-down on the draggable tile: drag a ghost and drop it onto a placeholder.
  // A real drag (past threshold) drops; a tap falls through to the existing click flow.
  const handleTileDragStart = (
    tile: TileDTO,
    clientX: number,
    clientY: number,
    rect: DOMRect,
    sourceScale: number,
    pointerId: number
  ) => {
    if (!active || !canvasRef.current) return;
    const canvas = canvasRef.current;

    // where inside the tile the user grabbed, in canvas-local (logical) px
    const offset = { x: (clientX - rect.left) / sourceScale, y: (clientY - rect.top) / sourceScale };
    let dragging = false;

    const handleMove = (e: PointerEvent) => {
      if (!dragging) {
        if (Math.hypot(e.clientX - clientX, e.clientY - clientY) < DRAG_THRESHOLD_PX) return;
        dragging = true;
        // capture on the stable canvas so move/up keep arriving even after the source tile
        // is replaced by the ghost. only once a real drag starts, so taps still click.
        try {
          canvas.setPointerCapture(pointerId);
        } catch {
          // ignore - capture is a best-effort robustness improvement
        }
      }
      const l = toCanvasLocal(e.clientX, e.clientY);
      setDragPos({ left: l.x - offset.x, top: l.y - offset.y });
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleCancel);
      try {
        canvas.releasePointerCapture(pointerId);
      } catch {
        // ignore - pointer may already be released
      }
      setDragPos(null);
    };
    const handleUp = (e: PointerEvent) => {
      if (dragging) handleDrop(e.clientX, e.clientY);
      cleanup();
    };
    const handleCancel = () => cleanup();

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleCancel);
  };

  const lastTileClick = (selectedAction: TileDTO) => {
    takeOverFromAgent();
    setSelectedAction(selectedAction);
  };

  const lastMeepleClick = (meeple: MeepleDTO) => {
    if (!posActions?.length) return;
    takeOverFromAgent();
    const defaultAction = posActions[0];
    setSelectedAction(defaultAction as TileDTO);
    // TODO play send back animation
    console.log('meepleClick', meeple);
  };

  const handleZoomChange = (value: number) => {
    setZoom(value);
  };

  const handleZoomIn = () => {
    setZoom(zoom + 0.1);
  };

  const handleZoomOut = () => {
    setZoom(zoom - 0.1);
  };

  const handleRotate = () => {
    if (!posActions || !selectedAction) return;
    takeOverFromAgent();
    // try to rotate forw
    let nextRotPos = posActions.find((action: TileDTO) => action.rot > selectedAction.rot);
    //try to rotate backw
    if (!nextRotPos) nextRotPos = posActions.find((action: TileDTO) => action.rot < selectedAction.rot);
    if (nextRotPos) {
      setSelectedAction(nextRotPos);
    }
  };

  const handleMeepleTypeChange = (type: MeepleType | undefined) => {
    takeOverFromAgent();
    setPreferredMeepleType(type);
  };

  const handleSubmit = () => {
    if (!active) return;
    if (!selectedAction) return;
    if (submitted) return;
    onSubmit(selectedAction);
    setBestAction(null);
    setSubmitted(true);
  };

  const handleSkip = () => {
    if (!active) return;
    if (submitted) return;
    onSubmit(undefined);
    setSelectedAction(null);
    setBestAction(null);
    setSubmitted(true);
  };

  // auto-submit the agent's pending move after the configured delay. Off when the player disabled
  // auto-submit (they submit manually), took over this turn, or paused the agent.
  const autoSubmitDelayMs = Math.max(1, myAgentSettings.autoSubmitDelay) * 1000;
  const autoSubmitArmed =
    !!bestAction && agentEnabled && myAgentSettings.autoSubmit && !agentPaused && !userTookOver;
  // per-second countdown so the Submit button can show "Submitting in Ns"; drives the actual submit
  // (single timer -> label and action stay in sync). Reset whenever the armed state changes.
  const [secsLeft, setSecsLeft] = useState<number | null>(null);
  useEffect(() => {
    setSecsLeft(autoSubmitArmed ? Math.max(1, myAgentSettings.autoSubmitDelay) : null);
  }, [autoSubmitArmed, myAgentSettings.autoSubmitDelay]);
  useInterval(
    () => setSecsLeft((s) => (s == null ? null : Math.max(0, s - 1))),
    autoSubmitArmed && secsLeft !== null && secsLeft > 0 ? 1000 : null
  );
  useEffect(() => {
    if (secsLeft === 0) {
      handleSubmit();
      setSecsLeft(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secsLeft]);

  // agent auto-skip: if the drawn tile has no legal placement, currentActions is empty and no move is
  // produced - so submit a skip (like clicking Skip) instead of stalling until the server times out.
  // Only when an (unpaused, auto-submitting) agent drives the seat; otherwise the human uses Skip.
  useInterval(
    function autoSkipWhenNoMoves() {
      handleSkip();
    },
    agentEnabled &&
      myAgentSettings.autoSubmit &&
      !agentPaused &&
      !userTookOver &&
      active &&
      game?.status === 'PLAYING' &&
      !submitted &&
      !bestAction &&
      currentActions.length === 0
      ? autoSubmitDelayMs
      : null
  );

  if (!tileData) return null;

  return (
    <div className={classes.tileMap}>
      <PrismaZoom scrollVelocity={0.05} currentZoom={zoom} onZoomChange={handleZoomChange}>
        <div className={classes.canvas} ref={canvasRef}>
          <div className={classes.bg}></div>
          {tileData.map((t, index) => {
            return <TileItem family={family} active={false} key={t.id} tile={t} last={index === tileData.length - 1} />;
          })}
          {lastTile &&
            active &&
            tilePlaces.map((p) => (
              <TilePlaceholder
                key={p.coord + '_' + lastTile.id}
                x={p.x}
                y={p.y}
                actions={p.actions}
                onClick={placeholderClick}
                family={family}
              />
            ))}
          {lastTile && active && selectedTilePos && posActions && !dragPos && (
            <TileItem
              family={family}
              key={lastTile.id}
              active={true}
              tile={selectedTilePos}
              posActions={posActions}
              onClick={lastTileClick}
              onDragStart={handleTileDragStart}
              onMeepleClick={lastMeepleClick}
              preferredMeepleType={preferredMeepleType}
            />
          )}
          {/* ghost tile following the cursor while dragging */}
          {lastTile && active && dragPos && (
            <TileItem
              family={family}
              active={true}
              tile={selectedAction ?? lastTile}
              tempPos={dragPos}
              dragging={true}
            />
          )}
          {recoveredMeeplesEvent?.recoveredMeeples.map((m) => {
            const meeple = cloneObject(m);
            meeple.recovered = false;
            // tile offset
            const t = tileData.filter(
              (t) => t.meeple?.playerId === meeple.playerId && t.meeple?.recovered && t?.meeple?.id === meeple.id
            )[0];
            if (!t || !t.meeple) return null;
            // ccson specific
            let left = t.x * tileWidth;
            let top = t.y * tileHeight;
            // add meeple offset
            const tileDef: TileDef = getTileDef(t.type);
            const nodes = tileDef.nodes;
            const meepleNode = nodes.find((node) => node.id === meeple.nodeId);
            const meepleOffset = calculateMeeplePos(meepleNode, t.rot) as CSSPos;
            console.log('recoveredMeepleOffset', t, left, top);
            left += meepleOffset.left as number;
            top += meepleOffset.top as number;
            const tileStyle = { width: 40, position: 'absolute' as const, left, top };
            return (
              <CSSTransition
                key={meeple.id}
                in={true}
                appear={true}
                timeout={3000}
                classNames="recover"
                unmountOnExit
                onEntered={() => {
                  setRecoveredMeeples(null);
                }}>
                <div style={tileStyle}>
                  <Meeple meeple={meeple} />
                </div>
              </CSSTransition>
            );
          })}
        </div>
      </PrismaZoom>
      {myPlayer && meeples && (
        <MeepleCont color={myPlayer.color} meeples={meeples} onMeepleTypeChange={handleMeepleTypeChange} />
      )}
      <div className={classes.rightBtnCont}>
        <Button onClick={handleZoomIn} small>
          <FaPlus />
        </Button>
        <Button onClick={handleZoomOut} small>
          <FaMinus />
        </Button>
        <Button onClick={handleRotate} small>
          <FiRotateCcw />
        </Button>
      </div>
      <TileAnchor zoom={zoom} dragEnabled={false}>
        {lastTile && active && !selectedPos && (
          <TileItem family={family} active={true} tile={lastTile} onDragStart={handleTileDragStart} />
        )}
      </TileAnchor>
      <div className={classes.submitBtn}>
        <Button onClick={handleSubmit} disabled={!submitBtnEnabled}>
          {autoSubmitArmed && secsLeft ? `Submitting in ${secsLeft}s` : 'Submit'}
        </Button>
      </div>
      <div className={classes.skipBtn}>
        <Button onClick={handleSkip} disabled={!submitBtnEnabled}>
          Skip
        </Button>
      </div>
      {agentEnabled && game?.status === 'PLAYING' && (
        <div className={classes.pauseBtn}>
          <Button secondary toggled={agentPaused} onClick={() => setAgentPaused((p) => !p)}>
            {agentPaused ? 'Resume agent' : 'Pause agent'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TileMap;
